import { OrderProductProps } from '@/interfaces/models/orderProduct';
import Ingredient from '@/models/ingredient';
import OrderProductIngredient from '@/models/orderProductIngredient';
import OrderProductInterface from '@/models/orderProductInterface';
import Product from '@/models/product';
import numberUtils from '@/utils/number.utils';
import stringHelper from '@/utils/stringHelper';

export default class OrderProduct implements OrderProductInterface {
  product: Product | null = null;
  ingredients: OrderProductIngredient[] = [];
  id: string = '';
  count: number = 1;
  discountAmount: number = 0;
  refundCount: number = 0;
  doneCount: number = 0;
  isChecked: boolean = false;
  gtins: string[] = [];
  boughtWithinPromotion: null | { id: string, name: string } = null;

  public static createFromRequest(productProps: OrderProductProps): OrderProduct {
    const orderProduct = new OrderProduct();

    orderProduct.product = Product.createFromRequest(productProps.product);
    orderProduct.ingredients = productProps.ingredients
      .map(OrderProductIngredient.createFromRequest)
      .sort((firstIngredient: OrderProductIngredient, secondIngredient: OrderProductIngredient) => {
        if (firstIngredient.ingredient && secondIngredient.ingredient) {
          return stringHelper.sortStrings(
            firstIngredient.ingredient.name,
            secondIngredient.ingredient.name
          );
        }

        return 0;
      });
    orderProduct.id = productProps.id;
    orderProduct.count = productProps.count;
    orderProduct.discountAmount = productProps.discountAmount;
    orderProduct.refundCount = productProps.refundCount;
    orderProduct.doneCount = productProps.doneCount;
    orderProduct.boughtWithinPromotion = productProps.boughtWithinPromotion;
    orderProduct.gtins = productProps.gtins;

    return orderProduct;
  }

  public static createByProductAndIngredients({ product, ingredients }: { product: Product, ingredients: Ingredient[] }) {
    const orderProduct = new OrderProduct();

    orderProduct.product = product;
    orderProduct.ingredients = ingredients.map(OrderProductIngredient.createByOriginalIngredient);

    return orderProduct;
  }

  get hash(): string {
    if (this.product !== null) {
      const ingredientsHash = this.ingredients
        .reduce((ingredientIdList: string[], orderProductIngredient: OrderProductIngredient) => {
          ingredientIdList.push(orderProductIngredient.hash);

          return ingredientIdList;
        }, [])
        .sort((firstId: string, secondId: string) => firstId > secondId ? 1 : -1)
        .reduce((hash: string, ingredientId: string | null) => hash + `-${ingredientId}`, '');

      return `${this.product.id}${ingredientsHash}`;
    }

    return '';
  }

  get cost() {
    return this.product !== null
      ? numberUtils.correctFloatNumber(
        this.product.cost + this.ingredientsCostWithoutDiscount - this.discountAmount
      )
      : 0;
  }

  get fullDiscountAmount() {
    const ingredientsDiscountAmount = this.ingredients.reduce((sum: number, ingredient: OrderProductIngredient) => {
      return numberUtils.correctFloatNumber(sum + ingredient.discountAmount * ingredient.count);
    }, 0);

    return numberUtils.correctFloatNumber(this.discountAmount + ingredientsDiscountAmount);
  }

  get ingredientsCostWithoutDiscount () {
    return this.ingredients.reduce(
      (sum: number, ingredient: OrderProductIngredient) => sum + ingredient.fullCost,
      0,
    );
  }

  get productCost() {
    return this.product !== null ? this.product.cost : 0;
  }

  get isHaveIngredients(): boolean {
    return this.ingredients.length > 0;
  }

  get isDone() {
    return this.doneCount === this.count;
  }

  public toggleChecked() {
    this.isChecked = !this.isChecked;
  }

  public done() {
    if (this.count > this.doneCount) {
      this.doneCount++;
    }
  }

  public undone() {
    if (this.doneCount > 0) {
      this.doneCount--;
    }
  }

  public increment(gtin: string) {
    if (gtin) {
      this.gtins.push(gtin);
    }
  }

  public decrement(gtin: string) {
    if (gtin) {
      const index = this.gtins.findIndex(( other: string ) => other === gtin);

      if (index !== -1) {
        this.gtins.splice(index, 1);
      }
    }
  }
}
